/**
 * text.ts — reducing untrusted text to something safe to render.
 *
 * Both the things a player types at other players — their display name and their
 * chat — go through here. One definition rather than two, because the rule below
 * is order-dependent in a way that is easy to get subtly wrong, and a second copy
 * would drift from it. It did: names and chat each had their own version, and both
 * deleted newlines instead of folding them.
 *
 * Rendering safety is a pair: this reduces the text, and the client writes it with
 * `textContent`. Nothing here escapes markup, deliberately — escaping into a
 * `textContent` render would show the player a literal `&lt;`.
 */
/**
 * Folds untrusted text into one short single line, or undefined if nothing
 * survives.
 *
 * THE ORDER OF THESE STEPS IS THE WHOLE FUNCTION. Newlines and tabs are control
 * characters, so stripping `\p{C}` first deletes them outright and welds together
 * the words either side: "hold\nthe\nline" comes out as "holdtheline". Whitespace
 * becomes a space first, and only then are the remaining control codes removed.
 *
 * The two classes are disjoint exactly where that matters. `\s` catches newlines,
 * tabs and the Unicode spaces. The codepoints that must be deleted rather than
 * spaced — zero-width space, the bidi overrides, the ones used to make a string
 * render as something other than what it says — are `\p{C}` and not `\s`.
 */
export function toSingleLine(raw, max) {
    if (typeof raw !== 'string')
        return undefined;
    const cleaned = raw
        .replace(/\s/g, ' ')
        .replace(/\p{C}/gu, '')
        .replace(/ +/g, ' ')
        .trim()
        .slice(0, max)
        // Truncation can land on a space, which would otherwise be left trailing.
        .trimEnd();
    return cleaned.length > 0 ? cleaned : undefined;
}
//# sourceMappingURL=text.js.map